import { useState } from 'react'
import { useMutation } from 'convex/react'
import { api } from 'convex/_generated/api'
import type { Id } from 'convex/_generated/dataModel'
import { Zap, Loader2 } from 'lucide-react'

type ReviewFrequency = 'light' | 'medium' | 'heavy'

interface DeckFrequencyPickerProps {
  deckId: Id<'flashcardDecks'>
  currentFrequency?: ReviewFrequency
}

const frequencyOptions = [
  {
    value: 'light' as ReviewFrequency,
    label: 'Light',
    description: '10 cards/day',
    color: 'text-sky-blue border-sky-blue',
    hoverColor: 'hover:bg-sky-blue/10',
  },
  {
    value: 'medium' as ReviewFrequency,
    label: 'Medium',
    description: '20 cards/day',
    color: 'text-terminal-green border-terminal-green',
    hoverColor: 'hover:bg-terminal-green/10',
  },
  {
    value: 'heavy' as ReviewFrequency,
    label: 'Heavy',
    description: '40 cards/day',
    color: 'text-hot-pink border-hot-pink',
    hoverColor: 'hover:bg-hot-pink/10',
  },
]

export function DeckFrequencyPicker({
  deckId,
  currentFrequency = 'medium',
}: DeckFrequencyPickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateFrequency = useMutation(api.flashcards.updateDeckFrequency)

  const handleFrequencyChange = async (frequency: ReviewFrequency) => {
    setIsLoading(true)
    setError(null)

    try {
      await updateFrequency({ deckId, frequency })
      setIsOpen(false)
    } catch (err) {
      console.error('Failed to update review frequency:', err)
      setError('Failed to update frequency. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const currentOption = frequencyOptions.find((opt) => opt.value === currentFrequency)

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 border-2 border-terminal-green text-terminal-green hover:bg-terminal-green/10 font-mono text-sm transition-all"
        aria-label="Change review frequency"
      >
        <Zap className="w-4 h-4" />
        <span>{currentOption?.label.toUpperCase()}</span>
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />

          {/* Dropdown Content */}
          <div className="absolute right-0 top-full mt-2 z-50 bg-black border-4 border-terminal-green shadow-xl w-80">
            <div className="p-4">
              {/* Header */}
              <div className="mb-4">
                <h3 className="text-terminal-green font-mono font-bold text-sm mb-1">
                  [REVIEW FREQUENCY]
                </h3>
                <p className="text-trail-tan font-mono text-xs">
                  Control daily card review load
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-3 p-2 border-2 border-red-500 bg-red-500/10">
                  <p className="text-red-500 font-mono text-xs">{error}</p>
                </div>
              )}

              {/* Frequency Options */}
              <div className="space-y-2">
                {frequencyOptions.map((option) => {
                  const isSelected = option.value === currentFrequency
                  return (
                    <button
                      key={option.value}
                      onClick={() => handleFrequencyChange(option.value)}
                      disabled={isLoading || isSelected}
                      className={`w-full p-3 border-2 ${option.color} ${option.hoverColor} transition-all disabled:opacity-50 disabled:cursor-not-allowed text-left ${
                        isSelected ? 'bg-terminal-green/20' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className={`font-mono font-bold text-sm ${option.color}`}>
                            {option.label}
                          </div>
                          <div className="text-trail-tan font-mono text-xs">
                            {option.description}
                          </div>
                        </div>
                        {isSelected && (
                          <div className="text-terminal-green font-mono text-xs">
                            ✓ ACTIVE
                          </div>
                        )}
                        {isLoading && option.value === currentFrequency && (
                          <Loader2 className="w-4 h-4 animate-spin text-terminal-green" />
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>

              {/* Info */}
              <div className="mt-4 p-3 border-2 border-sky-blue bg-sky-blue/5">
                <div className="text-sky-blue font-mono text-xs">
                  <div className="font-bold mb-1">ℹ INFO:</div>
                  <ul className="space-y-1 text-trail-tan">
                    <li>• Frequency controls daily new cards</li>
                    <li>• Review cards always appear when due</li>
                    <li>• Adjust based on your schedule</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
